import { type Dispatch, type SetStateAction } from 'react';
import { api } from '../shared/api-client.js';

export interface PendingAttachment {
  localId: string;
  file: File;
  status: 'uploading' | 'ready' | 'error';
  attachmentId?: string;
  fileName: string;
  mimeType: string;
  size: number;
  errorMessage?: string;
}

// Extensions only — browsers report text files inconsistently, and the backend
// allowlist in chat-attachment-service.ts keys off the extension for the same reason.
export const ACCEPTED_TYPES = [
  '.pdf,.jpg,.jpeg,.png,.gif,.webp,.docx,.xlsx,.xls,.pptx',
  '.txt,.md,.markdown,.csv,.tsv,.log',
  '.json,.jsonl,.yaml,.yml,.xml,.html,.htm',
  '.ts,.tsx,.js,.jsx,.py,.go,.java,.rb,.rs',
  '.sh,.sql,.css,.toml,.ini,.env,.conf',
].join(',');
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Stage and upload the given files as pending attachments, updating state as each
 * upload resolves. Shared by the paperclip picker, the welcome composer drop zone
 * and the active-thread composer drop zone so every entry point behaves identically
 * (same type/size validation, same uploading → ready/error lifecycle).
 *
 * What is already staged is rendered by ChatAttachmentTrayComponent, on its own row
 * above the composer — see the note there for why they are not together.
 */
export async function uploadPendingFiles(
  files: FileList | null,
  attachments: PendingAttachment[],
  onAttachmentsChange: Dispatch<SetStateAction<PendingAttachment[]>>,
): Promise<void> {
  if (!files || files.length === 0) return;

  const newAttachments: PendingAttachment[] = [];
  for (const file of Array.from(files)) {
    const localId = crypto.randomUUID();
    if (file.size > MAX_FILE_SIZE) {
      newAttachments.push({
        localId,
        file,
        status: 'error',
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        errorMessage: 'File exceeds 10 MB limit',
      });
      continue;
    }
    newAttachments.push({
      localId,
      file,
      status: 'uploading',
      fileName: file.name,
      mimeType: file.type,
      size: file.size,
    });
  }

  if (newAttachments.length === 0) return;
  onAttachmentsChange([...attachments, ...newAttachments]);

  // Upload each file (uploading state already set above)
  for (const pending of newAttachments) {
    if (pending.status === 'error') continue;
    const result = await api.uploadChatAttachment(pending.file);
    onAttachmentsChange((prev) =>
      prev.map((a) =>
        a.localId === pending.localId
          ? result.success
            ? { ...a, status: 'ready' as const, attachmentId: result.data.id }
            : { ...a, status: 'error' as const, errorMessage: result.error?.message ?? 'Upload failed' }
          : a,
      ),
    );
  }
}
